'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
	Home,
	User,
	Building2,
	LogIn,
	UserPlus,
	PartyPopper,
	BookOpenText,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
	name: string;
	url: string;
}

interface NavBarProps {
	items: NavItem[];
	className?: string;
}

export function NavBar({ items, className }: NavBarProps) {
	const pathname = usePathname();
	const [activeTab, setActiveTab] = useState(items[0]?.name || '');
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		// Function to determine which nav item should be active based on the current path
		const determineActiveTab = () => {
			// Find the nav item that best matches the current path
			const matchingItem = items.find((item) => {
				// Handle exact matches
				if (pathname === item.url) return true;
				// Handle nested routes (e.g., /events/[id] should match /events)
				if (item.url !== '/' && pathname.startsWith(item.url)) return true;
				// Handle home page
				if (item.url === '/' && pathname === '/') return true;
				return false;
			});

			if (matchingItem) {
				setActiveTab(matchingItem.name);
			}
		};

		determineActiveTab();
	}, [pathname, items]);

	// Icon map for dynamic icons
	const iconMap: Record<
		string,
		React.ComponentType<{ size: number; strokeWidth: number }>
	> = {
		Home,
		Profile: User,
		Dashboard: Building2,
		Events: PartyPopper,
		About: BookOpenText,
		Login: LogIn,
		'Sign Up': UserPlus,
	};

	return (
		<div
			className={cn(
				'fixed w-full left-1/2 -translate-x-1/2 z-50 flex justify-center',
				isMobile ? 'bottom-3 w-screen' : 'top-3'
			)}
		>
			{/* Restrict width for better styling */}
			<div className='flex items-center justify-center gap-3 bg-background/90 border border-border backdrop-blur-lg py-2 px-3 rounded-full shadow-lg w-fit max-w-[100vw] md:max-w-[600px]'>
				{items.map((item) => {
					const Icon = iconMap[item.name];
					const isActive = activeTab === item.name;

					return (
						<Link
							key={item.name}
							href={item.url}
							onClick={() => setActiveTab(item.name)}
							className={cn(
								'relative cursor-pointer text-sm font-semibold px-2 py-2 rounded-full transition-all',
								'text-foreground/80 hover:text-primary',
								isActive && 'bg-muted text-primary'
							)}
						>
							{/* Desktop View */}
							<span className='hidden md:inline'>{item.name}</span>

							{/* Mobile View */}
							<span className='md:hidden flex flex-col items-center'>
								{Icon && <Icon size={18} strokeWidth={2.5} />}
								<span className='text-xs text-muted-foreground/70 text-nowrap'>
									{item.name}
								</span>
							</span>

							{/* Active Indicator */}
							{isActive && (
								<>
									<motion.div
										layoutId='active-pill'
										className='absolute inset-0 w-full bg-primary/10 rounded-full -z-10'
										initial={false}
										transition={{
											type: 'spring',
											stiffness: 400,
											damping: 30,
										}}
									/>
									<div className='absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full'>
										<motion.div
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											className='absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2'
										/>
										<motion.div
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											className='absolute w-8 h-6 bg-primary/20 rounded-full blur-md -top-1'
										/>
										<motion.div
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											className='absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2'
										/>
									</div>
								</>
							)}
						</Link>
					);
				})}
			</div>
		</div>
	);
}
